import { apiUrl } from "@/services/studio/client";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getDefaultOrgId } from "@/utils/defaultOrg";

function currentOrgQuery(orgIdOverride?: string | null): string {
  const orgId = orgIdOverride || resolveCurrentOrgId();
  return orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";
}

function resolveCurrentOrgId(): string | null {
  const state = useAuthStoreV1.getState();
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const browserOrgId = readBrowserOrgId();
  if (browserOrgId) return browserOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

function readBrowserOrgId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("org_id");
    if (stored) return stored;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("org_id="));
    return cookie ? decodeURIComponent(cookie.slice("org_id=".length)) : null;
  } catch {
    return null;
  }
}

/** Build the absolute URL for downloading an artifact attached to a run. */
export function artifactUrl(runId: string, name: string, orgId?: string | null): string {
  return apiUrl(
    `/api/playbook-runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(name)}${currentOrgQuery(orgId)}`
  );
}

export function agentArtifactUrl(runId: string, name: string, orgId?: string | null): string {
  return apiUrl(
    `/api/agent-runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(name)}${currentOrgQuery(orgId)}`
  );
}

/**
 * Rewrite a markdown link href into a graph-view drill-down URL when it looks
 * like `MetadataType:full_name` (e.g. `ApexClass:UpdateKPI`). Returns the
 * original href otherwise so non-metadata links pass through untouched.
 */
export function maybeMetadataLink(href: string): string {
  if (!href) return href;
  // Allow optional `n:` / `node:` prefixes that some skills already emit.
  const stripped = href.replace(/^(?:n|node):/, "");
  const m = stripped.match(/^([A-Z][A-Za-z0-9]+):([^/?#]+)$/);
  if (!m) return href;
  const node = `${m[1]}:${m[2]}`;
  return `/org360?node=${encodeURIComponent(node)}`;
}
